import React, { useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { Controller, useForm } from 'react-hook-form'
import { X as XIcon } from 'react-feather'

import useSnackbar from 'hooks/useSnackbar'

import helpers from 'helpers'
import * as service from 'service'
import schema from './schema'

const ReactivateDialog = ({
  open,
  setOpen,
  companySupplier = {},
  onSuccess = () => {},
}) => {
  const [loading, setLoading] = useState(false)
  const snackbar = useSnackbar()

  const today = moment().startOf('day')
  const lastAudit = companySupplier?.lastAudit

  const isExistingPast = (value) => !!value && moment(value).isBefore(today)

  const dateToSendEmailsAllowsPast = isExistingPast(lastAudit?.dateToSendEmails)
  const endDateAllowsPast = isExistingPast(lastAudit?.endDate)

  const pastBlockProps = {
    disablePast: true,
    minDate: today,
    minDateMessage: 'A data não pode ser no passado',
  }

  const isPastDateInvalid = (value, originalValue) => {
    if (!value) return false
    if (moment(value).isSameOrAfter(today)) return false
    return !(
      originalValue &&
      moment(originalValue).isBefore(today) &&
      moment(value).isSame(moment(originalValue), 'day')
    )
  }

  const { control, handleSubmit, errors, setError, watch } = useForm({
    validationSchema: schema,
    defaultValues: {
      reactivateLastAudit: false,
      dateToSendEmails: lastAudit?.dateToSendEmails || null,
      endDate: lastAudit?.endDate || null,
    },
  })

  // D-17: reativar o fornecedor e reativar a avaliação dele são escolhas separadas. Só quando a
  // avaliação volta é que há régua de cobrança a agendar — e é por isso que as datas aparecem
  // condicionadas. Sem avaliação anterior não há o que reativar, e a opção nem é oferecida.
  const hasLastAudit = !!lastAudit
  const reactivateLastAudit = hasLastAudit && !!watch('reactivateLastAudit')

  const handleClose = () => {
    if (loading) return
    setOpen(false)
  }

  const onSubmit = async (data) => {
    if (reactivateLastAudit) {
      if (
        isPastDateInvalid(data.dateToSendEmails, lastAudit?.dateToSendEmails)
      ) {
        setError('dateToSendEmails', 'min', 'A data não pode ser no passado')
        return
      }

      if (isPastDateInvalid(data.endDate, lastAudit?.endDate)) {
        setError('endDate', 'min', 'A data não pode ser no passado')
        return
      }
    }

    try {
      setLoading(true)

      await service.dponet.suppliers.reactivate({
        companySupplierId: companySupplier?.id,
        reactivateLastAudit,
        ...(reactivateLastAudit && {
          dateToSendEmails: helpers.thirdPartyManegementPartners.formatDate(
            data.dateToSendEmails,
          ),
          endDate: helpers.thirdPartyManegementPartners.formatDate(
            data.endDate,
          ),
        }),
      })

      snackbar.open({
        message: reactivateLastAudit
          ? 'Fornecedor e avaliação reativados com sucesso'
          : 'Fornecedor reativado com sucesso',
        variant: 'success',
      })

      onSuccess()
      setOpen(false)
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Ocorreu um erro ao reativar o fornecedor',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box p={3}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h4">Reativar fornecedor</Typography>
          <IconButton onClick={handleClose} size="small">
            <XIcon size={20} />
          </IconButton>
        </Box>

        <Typography variant="body1" gutterBottom>
          {hasLastAudit
            ? 'Você pode reativar também a última avaliação deste fornecedor. Sem isso, o fornecedor volta a ficar ativo e nenhuma cobrança é enviada.'
            : 'O fornecedor voltará a ficar ativo. Ele ainda não possui avaliação anterior para reativar.'}
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid container spacing={2}>
            {hasLastAudit && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Controller
                      as={<Checkbox color="primary" />}
                      name="reactivateLastAudit"
                      control={control}
                      defaultValue={false}
                    />
                  }
                  labelPlacement="end"
                  label="Reativar a última avaliação"
                  onChange={([_, data]) => data}
                />
              </Grid>
            )}

            {reactivateLastAudit && (
              <>
                <Grid item xs={12}>
                  <Controller
                    as={
                      <KeyboardDatePicker
                        fullWidth
                        format="DD/MM/yyyy"
                        label="Quando os responsáveis devem ser notificados *"
                        helperText={errors?.dateToSendEmails?.message}
                        error={!!errors?.dateToSendEmails}
                        invalidDateMessage="Data inválida"
                        {...(!dateToSendEmailsAllowsPast && pastBlockProps)}
                      />
                    }
                    defaultValue={null}
                    control={control}
                    name="dateToSendEmails"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    as={
                      <KeyboardDatePicker
                        fullWidth
                        format="DD/MM/yyyy"
                        label="Prazo para resposta dos questionários *"
                        helperText={errors?.endDate?.message}
                        error={!!errors?.endDate}
                        invalidDateMessage="Data inválida"
                        {...(!endDateAllowsPast && pastBlockProps)}
                      />
                    }
                    defaultValue={null}
                    control={control}
                    name="endDate"
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gridGap={8}>
                <Button
                  onClick={handleClose}
                  variant="outlined"
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  Confirmar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Dialog>
  )
}

ReactivateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  companySupplier: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lastAudit: PropTypes.shape({
      dateToSendEmails: PropTypes.string,
      endDate: PropTypes.string,
    }),
  }),
  onSuccess: PropTypes.func,
}

export default ReactivateDialog
